import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import addMinutes from 'date-fns/addMinutes';
import format from 'date-fns/format';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import { useMemo, useState } from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  tv,
} from '@eluve/components';

interface TimeRangeSelectProps {
  /** Date of the time range */
  date?: Date;
  startTime?: Date;
  endTime?: Date;
  onStartTimeChange: (startTime?: Date) => void;
  onEndTimeChange: (endTime?: Date) => void;
  variant?: 'default' | 'subtle';
  disabled?: boolean;
}

const triggerVariant = tv({
  base: 'flex min-w-0 cursor-pointer items-center gap-2 rounded-sm border bg-transparent p-2 text-text-secondary outline-hidden',
  variants: {
    variant: {
      default:
        'border-bg-quaternary hover:border-border-secondary focus:border-border-accent-quaternary',
      subtle:
        'border-transparent hover:bg-bg-quaternary focus:border-bg-secondary',
    },
    disabled: {
      true: 'pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});

const timeSelectDropdown = tv({
  slots: {
    base: 'border border-border-primary p-0',
    header: 'grid grid-cols-2 gap-x-0 px-2 pt-2',
    dropdown:
      'flex h-[min(var(--radix-popper-available-height),304px)] flex-row px-2 pb-2',
    label: 'border-b p-2 text-sm',
    group: 'min-h-0 flex-1 overflow-auto',
    item: 'cursor-pointer font-mono',
  },
  variants: {
    selected: {
      true: {
        item: 'bg-bg-accent-strong-secondary text-white aria-selected:bg-bg-accent-strong-secondary aria-selected:text-white',
      },
    },
  },
});

const { base, header, dropdown, label, group, item } = timeSelectDropdown();

const TimeFormat = 'h:mm a';

export const TimeRangeSelect: React.FC<TimeRangeSelectProps> = ({
  date = new Date(),
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  variant,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const TimeOptions = useMemo(() => {
    const timeOptions = [];
    const startTime = setMinutes(setHours(date, 0), 0);
    const endTime = addDays(startTime, 1);

    let time = startTime;
    while (time <= endTime) {
      timeOptions.push(time);
      time = addMinutes(time, 15);
    }

    return timeOptions;
  }, [date]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button className={triggerVariant({ variant, disabled })}>
          <Icon name="Clock" />
          <Stack gap={1} wrap direction="horizontal">
            <span className="text-nowrap">
              {startTime ? format(startTime, TimeFormat) : 'Start '}
            </span>
            <Icon
              name="MoveHorizontal"
              size="xl"
              className="w-3 text-text-tertiary"
            />
            <span className="text-nowrap">
              {endTime ? format(endTime, TimeFormat) : 'End'}
            </span>
          </Stack>
          <Icon name="ChevronDown" className="ml-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        usesPortal={false}
        className={base()}
        collisionPadding={8}
      >
        <div className={header()}>
          <span className={label()}>Start Time</span>
          <span className={label()}>End Time</span>
        </div>
        <Command className={dropdown()}>
          <CommandGroup className={group({ class: 'border-r' })}>
            {TimeOptions.map((time) => {
              const formatted = format(time, TimeFormat);
              const selected =
                startTime && formatted === format(startTime, TimeFormat);

              return (
                <CommandItem
                  key={time.toISOString()}
                  onSelect={() => {
                    onStartTimeChange(time);
                    onEndTimeChange(addHours(time, 1));
                  }}
                  className={item({ selected })}
                  value={`start-time-${formatted}`}
                  ref={(el) => {
                    if (selected && el) {
                      requestAnimationFrame(() => {
                        el.scrollIntoView({ block: 'center' });
                      });
                    }
                  }}
                >
                  {formatted}
                </CommandItem>
              );
            })}
          </CommandGroup>

          <CommandGroup className={group()}>
            {TimeOptions.map((time) => {
              const formatted = format(time, TimeFormat);
              const selected =
                endTime && formatted === format(endTime, TimeFormat);
              const disabled = !startTime || time <= startTime;
              return (
                <CommandItem
                  key={time.toISOString()}
                  onSelect={() => {
                    onEndTimeChange(time);
                    setOpen(false);
                  }}
                  className={item({ selected })}
                  value={`end-time-${formatted}`}
                  disabled={disabled}
                  ref={(el) => {
                    if (selected && el) {
                      requestAnimationFrame(() => {
                        el.scrollIntoView({ block: 'center' });
                      });
                    }
                  }}
                >
                  {formatted}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
